/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';
import _unique from 'lodash/uniq';

import prepareSearchQuery from '../../helpers/assets/prepareSearchQuery';
import searchWithQuery from '../../helpers/products/search';

import { fetchThumbnails } from './fetchThumbnails';
import { clearPinnedSearch } from './pinEditLiveSearch';
import getProductGridThumbnailAssetId from './helpers/getProductGridThumbnailAssetId';
import { deselectAll } from './helpers/applySelection';
import { resolvePinnedSearchIds } from './helpers/pinnedSearch';

export const search = createAsyncThunk(
  'products/search',
  async (payload, {
    extra: { Logger, Toast },
    dispatch,
    getState,
  }) => {
    /** @TODO: sort */
    const page = payload?.page || 1;
    try {
      const query = prepareSearchQuery();
      const pin = getState().products?.ui?.pinnedSearch;
      const pinnedIds = resolvePinnedSearchIds(query, pin);
      if (pin && !pinnedIds && page === 1) {
        dispatch(clearPinnedSearch());
      }
      const { data } = await searchWithQuery(page, undefined, pinnedIds ? { ids: pinnedIds } : {});

      /** get thumbnails for products/variants */
      const assetIds = data.products
        .map(getProductGridThumbnailAssetId)
        .filter(Boolean);
      if (assetIds.length) dispatch(fetchThumbnails({ assetIds: _unique(assetIds) }));

      return data;
    } catch (error) {
      Toast(
        'Products search failed. Refresh and try again',
        { autoClose: false, type: 'error' },
      );
      Logger.error(new Error('Error products searching'), { error }, [
        'ProductsSearchFailed',
        { errorMessage: 'Products search failed.' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(
    search.pending,
    (state, { meta }) => {
      const { page = 1 } = meta.arg;
      if (page === 1) {
        state.search.items = [];
        state.search.total = 0;
        state.search.isLoading = true;
        // A new result set (filter/text/collection change) invalidates Search selection; loading
        // more pages of the SAME search (page > 1) must not touch it. Tree bag is independent
        // and must survive a products refetch (e.g. leaving /product for bulk-media). Drop any
        // in-flight Select All requestId so its onlyIds result cannot replace this new search.
        const keepTreeSelection = state.ui.selection.source === 'tree'
          && state.ui.selection.keys.length > 0;
        const keepPinnedSearchSelection = Boolean(
          state.ui.pinnedSearch?.ids?.length,
        );
        if (!keepTreeSelection && !keepPinnedSearchSelection) {
          state.ui.selection = deselectAll();
        }
        state.ui.isSelectingAll = false;
        state.ui.selectAllRequestId = null;
      } else {
        state.search.isPageLoading = true;
      }
      state.search.error = null;
    },
  ).addCase(
    search.fulfilled,
    (state, { payload, meta }) => {
      const { page = 1 } = meta.arg;
      if (page === 1) {
        state.search.isLoading = false;
        state.search.items = payload.products;
        const pinHasIds = Boolean(state.ui.pinnedSearch?.ids?.length);
        if (!pinHasIds && state.ui.selection.source !== 'tree') {
          // Pin was cleared on context mismatch; do not keep the old draft keys
          // on the new unpinned page. Tree bags must survive a products refetch.
          state.ui.selection = deselectAll();
        }
      } else {
        state.search.isPageLoading = false;
        state.search.items.push(...payload.products);
      }
      state.search.total = payload.total;
    },
  ).addCase(
    search.rejected,
    (state, { error }) => {
      state.search.isLoading = false;
      state.search.isPageLoading = false;
      state.search.error = error;
    },
  );
};
